import Axios from "axios";
import type { InternalAxiosRequestConfig } from "axios";
import { REFRESH_TOKENS_API } from "../constants/api-path";
import storage from "../helpers/storage";
import { useAuthStore } from "../redux/auth-store";

const ADMIN_API_BASE = `${process.env.REACT_APP_API_URL}/admin-apis`;

/** Dedicated client — no 401 refresh interceptor (prevents refresh loops). */
export const refreshHttpClient = Axios.create({
  baseURL: ADMIN_API_BASE,
  timeout: 10_000,
  headers: { Accept: "application/json" },
});

let isRefreshing = false;
let isLoggingOut = false;
let refreshPermanentlyFailed = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

export type AuthAxiosRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
};

export function logAuth(message: string, extra?: Record<string, unknown>) {
  console.info(`[PTT Auth] ${message}`, extra ?? "");
}

export function isRefreshUrl(url?: string): boolean {
  if (!url) return false;
  return url.includes("refresh-tokens");
}

export function isLoginUrl(url?: string): boolean {
  if (!url) return false;
  return (
    url.includes("/auth/login") ||
    url.includes("/auth/web-login") ||
    url.includes("web-login")
  );
}

export function isAuthExemptUrl(url?: string): boolean {
  return isRefreshUrl(url) || isLoginUrl(url);
}

export function resetAuthRefreshState(): void {
  refreshPermanentlyFailed = false;
  isRefreshing = false;
  isLoggingOut = false;
  failedQueue = [];
}

export function clearAuthSession(): void {
  storage.clearSession();
  useAuthStore.getState().setToken(null);
  useAuthStore.getState().setUser(null);
  refreshPermanentlyFailed = true;
}

function drainQueue(error: Error | null, token: string | null = null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else if (token) resolve(token);
    else reject(new Error("Refresh failed"));
  });
  failedQueue = [];
}

function redirectToLoginOnce(): void {
  const base = (process.env.REACT_APP_BASE_PATH || "").replace(/\/$/, "");
  const loginPath = `${base}/login` || "/login";
  const onLogin =
    window.location.pathname === loginPath ||
    window.location.pathname.endsWith("/login");
  if (!onLogin) {
    window.location.replace(loginPath);
  }
}

/** Logout once: clear tokens/session and redirect to login. */
export function logoutSession(reason: string): void {
  if (isLoggingOut) {
    logAuth("logout already in progress — skipped", { reason });
    return;
  }
  isLoggingOut = true;
  logAuth("refresh token failed - logging out", { reason });
  clearAuthSession();
  drainQueue(new Error("Session expired"));
  redirectToLoginOnce();
}

async function callRefreshEndpoint(): Promise<string> {
  const refreshTokenValue = storage.getRefreshToken();
  if (!refreshTokenValue) {
    throw new Error("No refresh token");
  }

  logAuth("refresh token started");
  const response = await refreshHttpClient.post(REFRESH_TOKENS_API, {
    refreshToken: refreshTokenValue,
  });

  const body = response.data;
  const payload = body?.data ?? body;
  const tokenBundle = payload?.token ?? payload;
  const access = tokenBundle?.access?.token;
  const refresh = tokenBundle?.refresh?.token;

  if (!access) {
    throw new Error("Invalid refresh response");
  }

  storage.setToken(access);
  if (refresh) {
    storage.setRefreshToken(refresh);
  }
  useAuthStore.getState().setToken("true");
  logAuth("refresh token success");
  return access;
}

/** Single in-flight refresh; queues concurrent 401 retries. */
export async function refreshAccessToken(): Promise<string> {
  if (refreshPermanentlyFailed) {
    logoutSession("refresh permanently disabled");
    throw new Error("Session expired");
  }

  if (!storage.getRefreshToken()) {
    logoutSession("no refresh token");
    throw new Error("No refresh token");
  }

  if (isRefreshing) {
    return new Promise<string>((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;
  try {
    const access = await callRefreshEndpoint();
    drainQueue(null, access);
    return access;
  } catch (error) {
    drainQueue(error as Error);
    logoutSession("refresh token request failed");
    throw error;
  } finally {
    isRefreshing = false;
  }
}

/** Proactive refresh (App interval) — never loops on failure. */
export async function tryProactiveRefresh(): Promise<boolean> {
  if (refreshPermanentlyFailed || isLoggingOut || isRefreshing) {
    return false;
  }
  if (!storage.getRefreshToken() || !storage.getToken()) {
    return false;
  }
  try {
    await refreshAccessToken();
    return true;
  } catch {
    return false;
  }
}
